import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { apiQueryAdapter } from "app/data/apiQueryAdapter";
import { QUERY_KEYS } from "app/data/common/queryKeys";
import PickupService from "app/data/pickup/pickupService";
import { PickupAssignRequestModel } from "app/data/pickup/models";

const pickupService = PickupService.getInstance();

export const useDriversByZIP = (
  zip: string,
  lastIds?: string
) => {
  const { data: driversDetails, isLoading: isLoadingDriversDetails, refetch: refetchDriversDetails } =
    useQuery({
      queryKey: [QUERY_KEYS.GET_DRIVERS_BY_ZIP, zip, lastIds],
      queryFn: async () => {
        const params = new URLSearchParams();
        if (zip) { 
          params.set("postalCode", zip); 
        }

        if(lastIds) {
          params.set("lastIds", lastIds);
        }
        
        const response = await apiQueryAdapter.query(
          `/pickups/drivers`,
          "GET",
          { params }
        );
        return response;

      },
      refetchOnWindowFocus: false,
      enabled: !!zip,
      retry: false,
    })
  return { driversDetails, isLoadingDriversDetails, refetchDriversDetails };
}

export const useAssignDrivers = () => {
  const queryClient = useQueryClient();
  const { mutate: assignDrivers, mutateAsync: assignPickupsAsync, isLoading: isAssigning } = useMutation({
    mutationFn: async (data: PickupAssignRequestModel[]) => {
      const response = await pickupService.changePickups(data);
      if (!response.ok()) {
        throw new Error(
          typeof response.getError === "function"
            ? String(response.getError())
            : "Failed to assign pickups"
        );
      }
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_STATIC_ROUTES] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PICKUP_ASSIGNMENT_DRIVERS_COLUMN] });
    },
    onError: (error) => {
      console.error("Failed to assign drivers:", error);
      const message =
        error instanceof Error ? error.message : "Could not assign or unassign pickups.";
      toast.error(message);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_STATIC_ROUTES] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PICKUP_ASSIGNMENT_DRIVERS_COLUMN] });
    },
  });

  return { assignDrivers, assignPickupsAsync, isAssigning };
};